import type { WaterKind } from '@/data/fish';
import { TILE_SIZE, type TileSpriteKey } from '@/sprites/tiles';

/**
 * Tile grid with axis-separated AABB collision.
 *
 * Resolving X and Y independently is what lets Katie slide along a wall instead
 * of sticking to it when walking diagonally into a corner — the single biggest
 * determinant of whether movement feels good.
 */

export interface TileKind {
  readonly sprite: TileSpriteKey;
  readonly solid: boolean;
  /**
   * Ground drawn underneath, for sprites with transparent pixels.
   *
   * Props are silhouettes, not full tiles — without a base layer their
   * transparency exposes the renderer's clear colour and they read as cut-outs
   * floating above the world rather than objects standing in it.
   */
  readonly base?: TileSpriteKey;
  /** Ground the hoe will break: grass and bare earth, not paths or floors. */
  readonly tillable?: boolean;
}

export type TileChar =
  | '.'
  | ','
  | '_'
  | '='
  | '~'
  | 'T'
  | '#'
  | 'o'
  // Water and shore
  | 'L'
  | 'r'
  | 'O'
  | 's'
  | 'S'
  // Interiors
  | 'W'
  | 'H'
  | 'C'
  | 'V'
  | 'F'
  | 'R'
  | 'B'
  | 'D'
  | 'M'
  | 'm'
  | '>'
  | '<'
  | 'K'
  | 'N';

// Annotated rather than `as const satisfies`: the latter narrows each entry to
// its own literal type, which drops the optional `base` from entries that omit
// it and makes the field unreadable at the call site.
export const TILE_KINDS: Readonly<Record<TileChar, TileKind>> = {
  '.': { sprite: 'grass', solid: false, tillable: true },
  ',': { sprite: 'grassDark', solid: false, tillable: true },
  '_': { sprite: 'dirt', solid: false, tillable: true },
  '=': { sprite: 'path', solid: false },
  '~': { sprite: 'water', solid: true },
  'L': { sprite: 'waterLake', solid: true },
  'r': { sprite: 'waterRiver', solid: true },
  'O': { sprite: 'waterOcean', solid: true },
  's': { sprite: 'sand', solid: false, tillable: false },
  'S': { sprite: 'wetSand', solid: false },
  'T': { sprite: 'tree', solid: true, base: 'grass' },
  '#': { sprite: 'fence', solid: true, base: 'grass' },
  'o': { sprite: 'rock', solid: true, base: 'grass' },

  'W': { sprite: 'wall', solid: true },
  'H': { sprite: 'roof', solid: true },
  'C': { sprite: 'cladding', solid: true },
  'V': { sprite: 'window', solid: true },
  'F': { sprite: 'floor', solid: false },
  'R': { sprite: 'rug', solid: false, base: 'floor' },
  'B': { sprite: 'bed', solid: true, base: 'floor' },
  'D': { sprite: 'doormat', solid: false, base: 'floor' },

  // Underground. Stairs sit on dungeon floor so the tile beneath them is not
  // a hole in the room when the sprite is only a marking.
  'M': { sprite: 'dungeonWall', solid: true },
  'm': { sprite: 'dungeonFloor', solid: false },
  '>': { sprite: 'stairsDown', solid: false, base: 'dungeonFloor' },
  '<': { sprite: 'stairsUp', solid: false, base: 'dungeonFloor' },

  // The travelling merchant's cart. Solid, so it is something you walk up to
  // and face rather than stand inside.
  'K': { sprite: 'cart', solid: true, base: 'grass' },

  // The notice board. Solid for the same reason the cart is: it is something
  // you walk up to and face.
  'N': { sprite: 'noticeBoard', solid: true, base: 'grass' },
};

/**
 * Which body of water a tile is, if it is one.
 *
 * Lives here rather than in the fishing code because it is a property of the
 * map: the same rod pulls different species out of a pond and the sea.
 */
export function waterKindOf(char: TileChar): WaterKind | null {
  switch (char) {
    case '~':
      return 'pond';
    case 'L':
      return 'lake';
    case 'r':
      return 'river';
    case 'O':
      return 'ocean';
    default:
      return null;
  }
}

export interface Aabb {
  x: number;
  y: number;
  readonly width: number;
  readonly height: number;
}

export class Tilemap {
  readonly width: number;
  readonly height: number;
  private readonly tiles: TileChar[];

  constructor(rows: readonly string[]) {
    if (rows.length === 0) throw new Error('Tilemap has no rows');
    this.height = rows.length;
    this.width = rows[0]!.length;
    this.tiles = [];

    for (const [y, row] of rows.entries()) {
      if (row.length !== this.width) {
        throw new Error(`Tilemap row ${y} is ${row.length} wide, expected ${this.width}`);
      }
      for (const char of row) {
        if (!(char in TILE_KINDS)) {
          throw new Error(`Tilemap uses unknown tile "${char}"`);
        }
        this.tiles.push(char as TileChar);
      }
    }
  }

  /** Out-of-bounds reads return the border tile so the world feels enclosed. */
  at(tileX: number, tileY: number): TileChar {
    if (tileX < 0 || tileY < 0 || tileX >= this.width || tileY >= this.height) return '~';
    return this.tiles[tileY * this.width + tileX]!;
  }

  kindAt(tileX: number, tileY: number): TileKind {
    return TILE_KINDS[this.at(tileX, tileY)];
  }

  isSolidTile(tileX: number, tileY: number): boolean {
    return this.kindAt(tileX, tileY).solid;
  }

  /** World-space pixel dimensions. */
  get pixelWidth(): number {
    return this.width * TILE_SIZE;
  }

  get pixelHeight(): number {
    return this.height * TILE_SIZE;
  }

  /** True if any tile overlapping the box is solid. */
  collides(box: Aabb): boolean {
    const minX = Math.floor(box.x / TILE_SIZE);
    const maxX = Math.floor((box.x + box.width - 1e-6) / TILE_SIZE);
    const minY = Math.floor(box.y / TILE_SIZE);
    const maxY = Math.floor((box.y + box.height - 1e-6) / TILE_SIZE);

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (this.isSolidTile(x, y)) return true;
      }
    }
    return false;
  }
}

export interface MoveResult {
  readonly x: number;
  readonly y: number;
  readonly hitX: boolean;
  readonly hitY: boolean;
}

/**
 * Move a box by (dx, dy), resolving each axis separately.
 *
 * Large steps are subdivided so a fast-moving body cannot tunnel through a
 * one-tile wall — relevant once dashes and knockback arrive in Phase 4.
 */
export function moveAndCollide(map: Tilemap, box: Aabb, dx: number, dy: number): MoveResult {
  const maxStep = TILE_SIZE / 2;
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / maxStep));
  const stepX = dx / steps;
  const stepY = dy / steps;

  const probe: Aabb = { x: box.x, y: box.y, width: box.width, height: box.height };
  let hitX = false;
  let hitY = false;

  for (let i = 0; i < steps; i++) {
    if (stepX !== 0) {
      probe.x += stepX;
      if (map.collides(probe)) {
        probe.x -= stepX;
        hitX = true;
      }
    }
    if (stepY !== 0) {
      probe.y += stepY;
      if (map.collides(probe)) {
        probe.y -= stepY;
        hitY = true;
      }
    }
  }

  return { x: probe.x, y: probe.y, hitX, hitY };
}
